import React, { Fragment } from "react";
import { Divider, HorizontalToolbar, ToolIcon } from "@theorgchartdev/storybook";
import SubchartsCard from "@/appComponents/Blocks/Cards/SubchartsCard/SubchartsCard";
import EditMenu from "@/appComponents/Navigation/MainToolbar/EditMenu/EditMenu";
import ViewMenu from "@/appComponents/Blocks/Menus/ViewMenu/ViewMenu";
import ZoomCanvasMenu from "@/appComponents/Navigation/MainToolbar/ZoomCanvasMenu/ZoomCanvasMenu";
import FieldDisplayMenu from "@/appComponents/Navigation/MainToolbar/FieldDisplayMenu/FieldDisplayMenu";
import CompositeChartSearch from "@/appComponents/Blocks/Composites/CompositeChartSearch/CompositeChartSearch";
import UndoRedoComposite from "@/appComponents/Navigation/MainToolbar/UndoRedoComposite/UndoRedoComposite";
import * as StateLib from "@/Utils/StateLib";
import { GetWSMgr } from "@/Utils/StateLib";
import { useNavigationContext } from "@/providers/NavigationProvider";
import ProfileToolbarAction from "./ProfileToolbarAction";
import { RecordInfoCard } from "@/appComponents/Blocks/Cards/RecordInfoCard/RecordInfoCard";
import { SourceInfoCard } from "@/appComponents/Blocks/Cards/SourceInfoCard/SourceInfoCard";
import { useRecordInfoContext } from "@/providers/RecordInfoProvider";
import { enumFeaturesList, enumUIModes, enumUISubModes, kROUser } from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";
import { IsBackgroundFile } from "@coreapp/utils/IsBackgroundFile";
import { isInViewManager } from "@coreapp/utils/isInViewManager";
import { getFeatureAccess } from "@/Utils/Misc";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import PlanSubmissionButtons from "@/appComponents/Navigation/MainToolbar/planning/PlanSubmissionButtons";
import ExportMenu from "@/appComponents/Navigation/Header/ExportMenu/ExportMenu";
import PlanButton from "@/appComponents/Navigation/MainToolbar/PlanButton/PlanButton";

const getToolbarVisibilityState = (permissions, doc, wsMgr) => {
  const curUIMode = Globals._uiConfig ? Globals._uiConfig.GetUIMode() : enumUIModes.chart;
  const subMode = wsMgr?.GetSubMode();
  const currentFilename = wsMgr?.GetFileName(true);
  const isBackground = currentFilename ? IsBackgroundFile(currentFilename) : false;
  const isPositionDocument = doc?.IsPositionDocument();
  const inWorkbenchMode =
    curUIMode === enumUIModes.edit || Globals._uiConfig.InBackgroundEditorMode();
  const inViewEditorMode = isInViewManager();
  const inEditMode =
    inWorkbenchMode ||
    inViewEditorMode ||
    (Globals._uiConfig.InBackgroundEditorMode() && !doc?.IsBackground());

  const isPlanningMode = curUIMode === enumUIModes.planning;

  const hasWorkbenchAccess = Globals._uiConfig.fHasWorkbench && permissions.canEditDocuments;
  const hasCustomChartFeature = getFeatureAccess(enumFeaturesList.customChart);

  const showCustomChartUpgradeGate =
    !(inEditMode || isBackground) &&
    hasWorkbenchAccess &&
    !!doc?.IsServerChart() &&
    !doc?.IsPlanChart() &&
    !hasCustomChartFeature &&
    permissions.showPricingAndPackaging;

  return {
    showChartExplorer: permissions.canAccessChartExplorer && Globals._uiConfig?.fEmbeddedMode,
    showSubcharts: permissions.canAccessSubcharts,
    showFields: permissions.canAccessInteractiveFields && !isPositionDocument,
    showGrid: false,
    showViews: permissions.showViews,
    showSearch: Globals._uiConfig.fHasSearch,
    showUndoRedo: inWorkbenchMode && permissions.canEditDocuments,
    showEdit:
      !isPlanningMode &&
      hasWorkbenchAccess &&
      (hasCustomChartFeature || permissions.showPricingAndPackaging),
    showZoom: true,
    showPlanSubmission: isPlanningMode && permissions.canAccessPlanApprovals,
    showExport: Globals._uiConfig?.fCanPublish && Globals._uiConfig?.fEmbeddedMode,
    inEditMode,
    showCustomChartUpgradeGate
  };
};

const MainToolbarDefault = () => {
  const {
    chartExplorer: { openChartExplorer }
  } = useNavigationContext();

  useRecordInfoContext();

  const permissions = usePermissionCheck();
  const doc = StateLib.GetDoc();
  const wsMgr = GetWSMgr();

  const {
    showChartExplorer,
    showSubcharts,
    showFields,
    showGrid,
    showViews,
    showSearch,
    showUndoRedo,
    showEdit,
    showZoom,
    showPlanSubmission,
    showExport,
    inEditMode,
    showCustomChartUpgradeGate
  } = getToolbarVisibilityState(permissions, doc, wsMgr);

  const [showSubchartsCard, setShowSubchartsCard] = React.useState(false);

  const leftContent = (
    <Fragment>
      {showChartExplorer && (
        <ToolIcon
          name={"compass"}
          size={"medium"}
          aria-label={"Chart explorer"}
          onClick={() => openChartExplorer()}
          data-testid="mtb-chart-explorer-btn"
          shape="round"
        />
      )}
      <ProfileToolbarAction />
      {showSubcharts && (
        <div>
          <ToolIcon
            name={"list-tree"}
            title={"Subcharts"}
            onClick={() => setShowSubchartsCard(true)}
            tooltipProps={{ message: appstr("Subcharts") }}
            id={"mtb-subcharts-btn"}
            data-testid="chart-mode-toolbar-subcharts"
            disabled={!getFeatureAccess(enumFeaturesList.subcharts)}
          />
          <SubchartsCard show={showSubchartsCard} onClose={() => setShowSubchartsCard(false)} />
        </div>
      )}
      {showGrid && <ToolIcon name={"table"} title={"Grid"} />}
      {showFields && <FieldDisplayMenu />}
      <div className={"ocn-toolbar-horizontal-divider"}>
        <Divider vertical={true} />
      </div>
      {showViews && (
        <div className="ocn-view-menu-wrapper">
          <ViewMenu />
        </div>
      )}
      {showZoom && <ZoomCanvasMenu />}
      <RecordInfoCard />
      <SourceInfoCard />
    </Fragment>
  );

  const centerContent = <Fragment>{showSearch && <CompositeChartSearch />}</Fragment>;

  const rightContent = (
    <Fragment>
      {showUndoRedo && <UndoRedoComposite />}
      {showEdit && (
        <EditMenu inEditMode={inEditMode} showCustomChartUpgradeGate={showCustomChartUpgradeGate} />
      )}
      <PlanButton />
      {showPlanSubmission && <PlanSubmissionButtons />}
      {showExport && <ExportMenu />}
    </Fragment>
  );

  return (
    <HorizontalToolbar
      leftContent={leftContent}
      centerContent={centerContent}
      rightContent={rightContent}
      size={"large"}
    />
  );
};

export default MainToolbarDefault;
